import axios from 'axios';
import dbUrl from '../utils/credentials';
import { fetchInvitationResponses } from './responses';
import { getLeafFields, getOptionLabel, normalizeField, slugifyInvitationName } from './invitation';

export function generateId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeBoardNote(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : null;
  if (!id) return null;
  return {
    id,
    text: typeof raw.text === 'string' ? raw.text : '',
    x: Number.isFinite(raw.x) ? raw.x : 0,
    y: Number.isFinite(raw.y) ? raw.y : 0,
  };
}

export const CARD_BUILTIN_FIELDS = [
  { key: 'surname', label: 'Cognome' },
  { key: 'phone', label: 'Telefono' },
  { key: 'email', label: 'Email' },
  { key: 'availability', label: 'Disponibilità' },
  { key: 'assignments', label: 'Numero assegnazioni' },
];

export const DEFAULT_CARD_FIELDS = CARD_BUILTIN_FIELDS.map((f) => f.key);

export function getDefaultCardConfig() {
  return { version: 1, fields: [...DEFAULT_CARD_FIELDS] };
}

// `version` mantiene l'oggetto su Firebase anche con `fields` vuoto.
export function normalizeCardConfig(raw) {
  if (!raw || typeof raw !== 'object') return getDefaultCardConfig();
  const fields = Array.isArray(raw.fields) ? raw.fields.filter((f) => typeof f === 'string') : [];
  return { version: 1, fields: [...new Set(fields)] };
}

export function normalizeEvent(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name || 'Evento senza nome',
    sourceInvitationId: raw.sourceInvitationId || '',
    fieldMapping: {
      name: raw.fieldMapping?.name || '',
      surname: raw.fieldMapping?.surname || '',
      phone: raw.fieldMapping?.phone || '',
      email: raw.fieldMapping?.email || '',
      availability: raw.fieldMapping?.availability || '',
    },
    stores: Array.isArray(raw.stores) ? raw.stores : [],
    assignments: Array.isArray(raw.assignments) ? raw.assignments : [],
    boardNotes: Array.isArray(raw.boardNotes)
      ? raw.boardNotes.map(normalizeBoardNote).filter(Boolean)
      : [],
    knownResponseIds: Array.isArray(raw.knownResponseIds) ? raw.knownResponseIds : [],
    slots: Array.isArray(raw.slots) ? raw.slots : [],
    cardConfig: normalizeCardConfig(raw.cardConfig),
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

export function getAnswerValue(response, fieldLabel) {
  if (!fieldLabel || !response?.answers) return null;
  const entry = Object.values(response.answers).find((a) => a.label === fieldLabel);
  return entry?.value ?? null;
}

export function formatAnswerValue(value) {
  if (typeof value === 'boolean') return value ? ['Sì'] : null;
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (value === null || value === undefined || value === '') return null;
  const str = String(value).trim();
  if (/[,;|]/.test(str)) {
    return str.split(/[,;|]/).map((s) => s.trim()).filter(Boolean);
  }
  return [str];
}

function parseSlotSortKey(slotLabel) {
  const match = String(slotLabel).match(/(\d{1,2}):(\d{2})/);
  if (!match) return { sortable: false, minutes: 0, label: slotLabel };
  const h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  return { sortable: true, minutes: h * 60 + m, label: slotLabel };
}

export function sortSlots(slots) {
  return [...new Set(slots.filter(Boolean))].sort((a, b) => {
    const ka = parseSlotSortKey(a);
    const kb = parseSlotSortKey(b);
    if (ka.sortable && kb.sortable) return ka.minutes - kb.minutes;
    if (ka.sortable && !kb.sortable) return -1;
    if (!ka.sortable && kb.sortable) return 1;
    return String(a).localeCompare(String(b), 'it');
  });
}

export function extractSlotsFromResponses(responses, fieldMapping, invitationFields = []) {
  const slots = new Set();

  responses.forEach((r) => {
    const val = getAnswerValue(r, fieldMapping.availability);
    const formatted = formatAnswerValue(val);
    if (formatted) formatted.forEach((s) => slots.add(s));
  });

  const field = getLeafFields(invitationFields).find((f) => f.name === fieldMapping.availability);
  if (field?.options) {
    field.options.forEach((opt) => slots.add(getOptionLabel(opt)));
  }

  return sortSlots([...slots]);
}

export function mergeEventSlots(event, responses, fieldMapping, invitationFields = []) {
  const fromResponses = extractSlotsFromResponses(responses, fieldMapping, invitationFields);
  const fromSaved = event.slots || [];
  const fromAssignments = (event.assignments || []).map((a) => a.slot);
  return sortSlots([...fromResponses, ...fromSaved, ...fromAssignments]);
}

export function normalizeStore(store, globalSlots = []) {
  const base = Array.isArray(store.slotOrder) && store.slotOrder.length
    ? [...store.slotOrder]
    : [...globalSlots];
  globalSlots.forEach((s) => {
    if (s && !base.includes(s)) base.push(s);
  });
  return { ...store, slotOrder: base.filter(Boolean) };
}

export function getStoreSlotOrder(store, globalSlots = []) {
  return normalizeStore(store, globalSlots).slotOrder;
}

export function syncStoresSlotOrders(event) {
  const globalSlots = event.slots || [];
  return {
    ...event,
    stores: event.stores.map((s) => normalizeStore(s, globalSlots)),
  };
}

export function reorderStoreSlots(event, storeId, fromSlot, toSlot) {
  if (fromSlot === toSlot) return event;
  const store = event.stores.find((s) => s.id === storeId);
  if (!store) return event;
  const order = [...getStoreSlotOrder(store, event.slots || [])];
  const oldIndex = order.indexOf(fromSlot);
  const newIndex = order.indexOf(toSlot);
  if (oldIndex < 0 || newIndex < 0) return event;
  const [removed] = order.splice(oldIndex, 1);
  order.splice(newIndex, 0, removed);
  return updateStore(event, storeId, { slotOrder: order });
}

function buildParticipantExtras(response) {
  const extras = {};
  Object.values(response.answers || {}).forEach((answer) => {
    if (!answer?.label) return;
    const values = formatAnswerValue(answer.value);
    if (values?.length) extras[answer.label] = values.join(', ');
  });
  return extras;
}

export function buildParticipantFromResponse(response, fieldMapping) {
  const nameVal = getAnswerValue(response, fieldMapping.name);
  const surnameVal = fieldMapping.surname ? getAnswerValue(response, fieldMapping.surname) : '';
  const availabilityVal = getAnswerValue(response, fieldMapping.availability);
  const availability = formatAnswerValue(availabilityVal) || [];

  const name = typeof nameVal === 'string' ? nameVal : (nameVal ? String(nameVal) : '');
  const surname = typeof surnameVal === 'string' ? surnameVal : (surnameVal ? String(surnameVal) : '');

  return {
    responseId: response.id,
    name,
    surname,
    phone: getAnswerValue(response, fieldMapping.phone) || '',
    email: getAnswerValue(response, fieldMapping.email) || '',
    availabilitySlots: availability,
    extras: buildParticipantExtras(response),
    submittedAt: response.submittedAt,
  };
}

export function formatParticipantDisplayName(participant) {
  const parts = [participant.name, participant.surname].filter(Boolean);
  const full = parts.join(' ').trim();
  return full || 'Senza nome';
}

export function getParticipantContactLine(participant) {
  return [participant.phone, participant.email].filter(Boolean).join(' · ');
}

export function buildParticipants(responses, fieldMapping, invitationFields = []) {
  const mapping = invitationFields.length
    ? enrichFieldMapping(fieldMapping, invitationFields)
    : fieldMapping;
  return responses.map((r) => buildParticipantFromResponse(r, mapping));
}

export async function fetchAllEvents() {
  const res = await axios.get(`${dbUrl}events.json`);
  if (!res.data) return [];
  return Object.entries(res.data)
    .map(([id, ev]) => normalizeEvent({ id, ...ev }))
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

export async function fetchEvent(eventId) {
  const res = await axios.get(`${dbUrl}events/${eventId}.json`);
  if (!res.data) return null;
  return normalizeEvent({ id: eventId, ...res.data });
}

export async function saveEvent(event) {
  const { id, ...payload } = event;
  await axios.put(`${dbUrl}events/${id}.json`, {
    ...payload,
    updatedAt: Date.now(),
  });
}

export async function deleteEvent(eventId) {
  await axios.delete(`${dbUrl}events/${eventId}.json`);
}

export async function resolveUniqueEventId(baseName) {
  const base = slugifyInvitationName(baseName) || `evento-${Date.now()}`;
  let candidate = base;
  let counter = 2;
  while (counter < 100) {
    try {
      const res = await axios.get(`${dbUrl}events/${candidate}.json`);
      if (!res.data) return candidate;
    } catch {
      return candidate;
    }
    candidate = `${base}-${counter}`;
    counter += 1;
  }
  return candidate;
}

export async function loadInvitationWithResponses(invitationId) {
  const invRes = await axios.get(`${dbUrl}invitations/${invitationId}.json`);
  const invitation = invRes.data;
  if (!invitation) return null;
  const responses = await fetchInvitationResponses(invitationId, invitation.responses);
  const fields = (invitation.fields || []).map(normalizeField);
  return { invitation, responses, fields };
}

export function syncKnownResponses(event, responses) {
  const previous = new Set(event.knownResponseIds || []);
  const newIds = responses.map((r) => r.id).filter((id) => !previous.has(id));
  return {
    event: {
      ...event,
      knownResponseIds: responses.map((r) => r.id),
    },
    newResponseIds: newIds,
  };
}

export function getVisibleParticipants(participants, assignments, showAssigned) {
  if (showAssigned) return participants;
  return getUnassignedParticipants(participants, assignments);
}

export function getUnassignedParticipants(participants, assignments) {
  const assigned = new Set(assignments.map((a) => a.responseId));
  return participants.filter((p) => !assigned.has(p.responseId));
}

export function addAssignment(event, { responseId, storeId, slot }) {
  const exists = event.assignments.some(
    (a) => a.responseId === responseId && a.storeId === storeId && a.slot === slot
  );
  if (exists) return event;
  return {
    ...event,
    assignments: [
      ...event.assignments,
      { id: generateId('asn'), responseId, storeId, slot },
    ],
  };
}

export function removeAssignment(event, assignmentId) {
  return {
    ...event,
    assignments: event.assignments.filter((a) => a.id !== assignmentId),
  };
}

export function moveAssignment(event, assignmentId, storeId, slot) {
  return {
    ...event,
    assignments: event.assignments.map((a) =>
      a.id === assignmentId ? { ...a, storeId, slot } : a
    ),
  };
}

export function addStore(event, name) {
  return {
    ...event,
    stores: [
      ...event.stores,
      {
        id: generateId('store'),
        name,
        notes: '',
        leaderResponseId: null,
        viceLeaderResponseId: null,
        slotOrder: [...(event.slots || [])],
      },
    ],
  };
}

export function updateStore(event, storeId, patch) {
  return {
    ...event,
    stores: event.stores.map((s) => (s.id === storeId ? { ...s, ...patch } : s)),
  };
}

export function removeStore(event, storeId) {
  return {
    ...event,
    stores: event.stores.filter((s) => s.id !== storeId),
    assignments: event.assignments.filter((a) => a.storeId !== storeId),
  };
}

export function addBoardNote(event, { text = '', x = 0, y = 0 } = {}) {
  const note = {
    id: generateId('note'),
    text,
    x,
    y,
  };
  return {
    ...event,
    boardNotes: [...(event.boardNotes || []), note],
  };
}

export function updateBoardNote(event, noteId, patch) {
  return {
    ...event,
    boardNotes: (event.boardNotes || []).map((note) =>
      (note.id === noteId ? { ...note, ...patch } : note)
    ),
  };
}

export function removeBoardNote(event, noteId) {
  return {
    ...event,
    boardNotes: (event.boardNotes || []).filter((note) => note.id !== noteId),
  };
}

export function guessFieldMapping(fields) {
  const names = getLeafFields(fields).map((f) => f.name);
  const find = (...candidates) =>
    names.find((n) => candidates.some((c) => n.toLowerCase().includes(c))) || '';
  const findName = () =>
    names.find((n) => {
      const lower = n.toLowerCase();
      return (lower.includes('nome') || lower.includes('name'))
        && !lower.includes('cognome')
        && !lower.includes('surname');
    }) || find('nome', 'name');

  return {
    name: findName(),
    surname: find('cognome', 'surname'),
    phone: find('telefono', 'tel', 'cell'),
    email: find('email', 'mail'),
    availability: find('turno', 'disponib', 'slot', 'fascia'),
  };
}

export function enrichFieldMapping(mapping, fields) {
  const guessed = guessFieldMapping(fields);
  return {
    ...mapping,
    surname: mapping.surname || guessed.surname || '',
  };
}

export function countParticipantAssignments(responseId, assignments) {
  return (assignments || []).filter((a) => a.responseId === responseId).length;
}

export function countAssignedParticipants(participants, assignments) {
  const assignedIds = new Set((assignments || []).map((a) => a.responseId));
  return (participants || []).filter((p) => assignedIds.has(p.responseId)).length;
}

export function participantMatchesSlot(participant, slotLabel) {
  const availability = participant?.availabilitySlots || [];
  if (availability.length === 0) return true;
  const slot = String(slotLabel).toLowerCase();
  return availability.some((value) => {
    const avail = String(value).toLowerCase();
    return avail === slot || avail.includes(slot) || slot.includes(avail);
  });
}

export function matchesParticipantSearch(participant, term) {
  if (!term) return true;
  const t = term.toLowerCase();
  return (
    participant.name?.toLowerCase().includes(t)
    || participant.surname?.toLowerCase().includes(t)
    || formatParticipantDisplayName(participant).toLowerCase().includes(t)
    || String(participant.email || '').toLowerCase().includes(t)
    || String(participant.phone || '').toLowerCase().includes(t)
  );
}

export function matchesSlotSearch(slotLabel, term) {
  if (!term) return true;
  return String(slotLabel).toLowerCase().includes(term.toLowerCase());
}

export function filterParticipantsForPlanner(participants, searchTerm, searchMode, slotFilter = '') {
  let result = participants;
  const slot = slotFilter.trim();

  if (slot) {
    result = result.filter((p) =>
      p.availabilitySlots?.some((s) => String(s).toLowerCase() === slot.toLowerCase())
    );
  }

  const term = searchTerm.trim();
  if (!term) return result;

  if (searchMode === 'slot') {
    return result.filter((p) =>
      p.availabilitySlots?.some((s) => matchesSlotSearch(s, term))
    );
  }

  return result.filter((p) => matchesParticipantSearch(p, term));
}

function sortResponsesBySubmittedAt(responses) {
  return [...responses].sort((a, b) => {
    const ta = new Date(a.submittedAt || 0).getTime();
    const tb = new Date(b.submittedAt || 0).getTime();
    return ta - tb;
  });
}

export function resolveResponseId(responseId, responses) {
  if (!responseId) return null;
  if (responses.some((r) => r.id === responseId)) return responseId;

  const legacyMatch = String(responseId).match(/^legacy_(\d+)$/);
  if (legacyMatch) {
    return resolveResponseIdByIndex(parseInt(legacyMatch[1], 10), responses);
  }

  const indexedMatch = String(responseId).match(/^r_(\d+)$/);
  if (indexedMatch) {
    return resolveResponseIdByIndex(parseInt(indexedMatch[1], 10), responses);
  }

  return null;
}

function resolveResponseIdByIndex(index, responses) {
  const byLegacyIndex = responses.find((r) => r._legacyIndex === index);
  if (byLegacyIndex) return byLegacyIndex.id;

  const byLegacyId = responses.find((r) => r.id === `legacy_${index}`);
  if (byLegacyId) return byLegacyId.id;

  const byIndexedId = responses.find((r) => r.id === `r_${index}`);
  if (byIndexedId) return byIndexedId.id;

  const sorted = sortResponsesBySubmittedAt(responses);
  return sorted[index]?.id ?? null;
}

export function reconcileEventWithResponses(event, responses) {
  const responseIds = new Set(responses.map((r) => r.id));
  let migratedCount = 0;
  let removedCount = 0;
  const seen = new Set();
  const assignments = [];

  for (const asn of event.assignments || []) {
    const resolvedId = resolveResponseId(asn.responseId, responses);
    if (!resolvedId || !responseIds.has(resolvedId)) {
      removedCount += 1;
      continue;
    }

    if (resolvedId !== asn.responseId) migratedCount += 1;

    const key = `${resolvedId}|${asn.storeId}|${asn.slot}`;
    if (seen.has(key)) {
      removedCount += 1;
      continue;
    }
    seen.add(key);

    assignments.push(
      resolvedId === asn.responseId ? asn : { ...asn, responseId: resolvedId }
    );
  }

  const resolveRef = (refId) => {
    if (!refId) return null;
    const resolved = resolveResponseId(refId, responses);
    return resolved && responseIds.has(resolved) ? resolved : null;
  };

  const stores = (event.stores || []).map((store) => ({
    ...store,
    leaderResponseId: resolveRef(store.leaderResponseId),
    viceLeaderResponseId: resolveRef(store.viceLeaderResponseId),
  }));

  return {
    event: {
      ...event,
      assignments,
      stores,
      knownResponseIds: responses.map((r) => r.id),
    },
    migratedCount,
    removedCount,
  };
}

export function getResolvedSlotAssignments(store, slot, assignments, participants) {
  const participantIds = new Set(participants.map((p) => p.responseId));
  return assignments.filter(
    (a) =>
      a.storeId === store.id
      && a.slot === slot
      && participantIds.has(a.responseId)
  );
}
